import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { projectsApi } from '../api/projects';
import { seriesBookBoardApi } from '../api/seriesBookBoard';
import { seriesSnapshotsApi } from '../api/seriesSnapshots';
import { storiesApi } from '../api/stories';
import {
  PROPOSITION_WORD_CAP,
  START_NOTE,
  boardPayload,
  clampProposition,
  itemsFromBoard,
  mergeTimelineItems,
  seedBooksFromStories,
  shortLine,
  wordCount,
} from '../utils/seriesBookBoard';
import { openStartedBook } from '../utils/openStartedBook';
import { FROM_TALKS_AND_NOTES, seriesOffersPath } from '../utils/seriesOffers';
import { SERIES_PLANNING_TOOLS } from '../utils/seriesBeta';
import {
  BOOK_LINE_EMPTY,
  BOOK_LINE_LABEL,
  BOOK_LINE_PLACEHOLDER,
  BOOKS_IN_PILE_LEDE,
  NO_TIMELINE_BOOKS,
  bookStatusLabel,
  patchSharedBook,
  sharedBooksFromBoard,
  startSharedBook,
} from '../utils/seriesSharedBooks';
import {
  MAKE_A_CARD,
  OPEN_WRITEUP,
  SNAPSHOT_EMPTY,
  cardsOfKind,
  emptySnapshots,
  findSeriesCard,
  hasCardWriteup,
  newCardId,
  removeCard,
  upsertCard,
  worldCard,
} from '../utils/seriesSnapshots';
import '../components/BookSheet/BookSheet.css';

/**
 * Series Notes (#676 / #696 / #700). People / world / custom at series
 * altitude, plus the same Timeline book objects (same id). One Snapshots
 * room. Books are not copied into series_snapshots. Click a note for
 * the short version. A button on that note opens the long write-up.
 * Thin notes stay on this page. Writer-invoked. Empty tiles stay Make
 * a note. Make notes from chats and docs is a separate door. No silent
 * fill. Start this book remains the only way to make a real book.
 */
export default function SeriesCardsPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    currentProject,
    setCurrentProject,
    setCurrentStory,
    setProjectAndStory,
    stories,
    refreshStories,
    createStory,
  } = useApp();

  const [project, setProject] = useState(
    currentProject?.id === projectId ? currentProject : null,
  );
  const [store, setStore] = useState(emptySnapshots());
  const [boardItems, setBoardItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [bookDraft, setBookDraft] = useState(null);
  const [showWriteup, setShowWriteup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [justStartedId, setJustStartedId] = useState(null);

  useEffect(() => {
    setCurrentStory(null);
  }, [setCurrentStory]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectId) return;
      if (currentProject?.id === projectId) {
        setProject(currentProject);
        return;
      }
      try {
        const next = await projectsApi.get(projectId);
        if (!cancelled && next) {
          setProject(next);
          setCurrentProject(next);
        }
      } catch {
        if (!cancelled) setProject(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, currentProject, setCurrentProject]);

  const loadStore = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [data, board] = await Promise.all([
        seriesSnapshotsApi.get(projectId),
        seriesBookBoardApi.get(projectId),
      ]);
      setStore(data?.cards ? data : emptySnapshots());
      // Full sequence so a book save does not drop gaps or pins.
      let items = itemsFromBoard(board);
      if (!items.some((item) => item.type === 'book')) {
        // Display only, same fill the Timeline shows; the Timeline owns the seed write.
        const list = await storiesApi.list(projectId).catch(() => []);
        if (list?.length) items = itemsFromBoard({ items: seedBooksFromStories(list) });
      }
      setBoardItems(items);
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not load Series Notes.');
      setStore(emptySnapshots());
      setBoardItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadStore(); }, [loadStore]);

  const people = useMemo(() => cardsOfKind(store, 'people'), [store]);
  const custom = useMemo(() => cardsOfKind(store, 'custom'), [store]);
  const world = useMemo(() => worldCard(store), [store]);
  const books = useMemo(
    () => sharedBooksFromBoard({ items: boardItems }),
    [boardItems],
  );

  useEffect(() => {
    if (loading) return;
    const kind = searchParams.get('kind');
    const name = searchParams.get('name');
    if (!kind && !name) return;
    const card = findSeriesCard(store, { kind, name });
    if (!card) return;
    setBookDraft(null);
    setDraft({
      id: card.id,
      kind: card.kind,
      name: card.name || (card.kind === 'world' ? 'World' : ''),
      description: card.description || '',
      writeup: card.writeup || '',
    });
    setShowWriteup(searchParams.get('writeup') === '1' && hasCardWriteup(card));
  }, [loading, store, searchParams]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const saved = await seriesSnapshotsApi.put(projectId, next);
      setStore(saved?.cards ? saved : next);
      setError(null);
      setDraft(null);
      setShowWriteup(false);
    } catch (err) {
      setError(err.message || 'Could not save this note.');
    } finally {
      setSaving(false);
    }
  };

  const persistBoard = async (nextItems) => {
    setSaving(true);
    try {
      const toSave = mergeTimelineItems(boardItems, nextItems);
      const saved = await seriesBookBoardApi.put(projectId, boardPayload(toSave));
      const resolved = itemsFromBoard(saved);
      setBoardItems(mergeTimelineItems(
        resolved.length ? resolved : toSave,
        toSave,
      ));
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not save this book.');
    } finally {
      setSaving(false);
    }
  };

  const startDraft = (kind, existing) => {
    setBookDraft(null);
    setDraft({
      id: existing?.id || newCardId(),
      kind,
      name: existing?.name || (kind === 'world' ? 'World' : ''),
      description: existing?.description || '',
      writeup: existing?.writeup || '',
    });
    setShowWriteup(false);
  };

  const openBookCard = (book) => {
    setDraft(null);
    setShowWriteup(false);
    setBookDraft({
      id: book.id,
      title: book.title || '',
      proposition: book.proposition || '',
      status: book.status || 'idea',
      story_id: book.story_id || null,
    });
  };

  const saveDraft = () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (draft.kind !== 'world' && !name) {
      setError('Give this note a name.');
      return;
    }
    persist(upsertCard(store, {
      ...draft,
      name: name || 'World',
      description: draft.description.trim(),
      writeup: (draft.writeup || '').trim(),
    }));
  };

  const saveBookDraft = () => {
    if (!bookDraft?.id) return;
    const title = (bookDraft.title || '').trim();
    if (!title) {
      setError('Give this book a title.');
      return;
    }
    const next = patchSharedBook(boardItems, bookDraft.id, {
      title,
      proposition: clampProposition(bookDraft.proposition || '').trim(),
    });
    persistBoard(next);
  };

  const deleteDraft = () => {
    if (!draft?.id) return;
    persist(removeCard(store, draft.id));
  };

  const startBook = async () => {
    if (!projectId || !bookDraft || bookDraft.status === 'started' || startingId) return;
    setStartingId(bookDraft.id);
    setError(null);
    try {
      const payload = {
        name: bookDraft.title,
        description: bookDraft.proposition || null,
      };
      const story = createStory
        ? await createStory(projectId, payload)
        : await storiesApi.create(projectId, payload);
      if (story?.id) {
        await storiesApi.ensureDiscoverySession(story.id);
      }
      const next = startSharedBook(boardItems, bookDraft.id, story.id);
      await persistBoard(next);
      setBookDraft({
        ...bookDraft,
        status: 'started',
        story_id: story.id,
      });
      setJustStartedId(bookDraft.id);
      await refreshStories?.();
      const project = currentProject?.id === projectId
        ? currentProject
        : await projectsApi.get(projectId);
      await openStartedBook(story, navigate, setProjectAndStory, project);
    } catch (err) {
      setError(err.message || 'Could not start this book.');
    } finally {
      setStartingId(null);
    }
  };

  const openBook = async () => {
    if (!bookDraft?.story_id) return;
    const fromList = (stories || []).find((row) => row.id === bookDraft.story_id);
    const story = fromList || await storiesApi.get(bookDraft.story_id);
    const nextProject = currentProject?.id === projectId
      ? currentProject
      : await projectsApi.get(projectId);
    await openStartedBook(story, navigate, setProjectAndStory, nextProject);
  };

  const renderEmptyTile = (kind, label) => (
    <button
      type="button"
      className="bs-tile bs-tile--empty"
      onClick={() => startDraft(kind)}
    >
      <div className="bs-tile__header">
        <h3 className="bs-tile__title">{label}</h3>
      </div>
      <p className="bs-tile__cta">{SNAPSHOT_EMPTY}</p>
      <p className="bs-tile__cta">{MAKE_A_CARD}</p>
    </button>
  );

  if (loading) return <div className="bs-panel"><p>Loading…</p></div>;

  const existingDraft = draft && store.cards.some((card) => card.id === draft.id);
  const writeupReady = hasCardWriteup(draft);

  return (
    <div className="bs-panel">
      <div className="bs-header">
        <div>
          <h1>Series Notes</h1>
          <p className="bs-header__subtitle">{project?.name || ''}</p>
        </div>
        {SERIES_PLANNING_TOOLS && (
          <Link className="bs-add-link" to={seriesOffersPath(projectId)}>
            {FROM_TALKS_AND_NOTES}
          </Link>
        )}
      </div>

      {error && (
        <div className="bs-error">
          {error}{' '}
          <button type="button" className="bs-btn--danger" onClick={() => setError(null)}>dismiss</button>
        </div>
      )}

      <div className="bs-content">
        <div className="bs-custom-section">
          <h2 className="bs-custom-section__title">Books</h2>
          <p className="bs-tile__cta">{BOOKS_IN_PILE_LEDE}</p>
          {books.length === 0 && (
            <p className="bs-tile__cta">
              {NO_TIMELINE_BOOKS}
              {' '}
              <Link to={`/projects/${projectId}/series`}>Open Timeline</Link>
            </p>
          )}
          {books.length > 0 && (
            <div className="bs-grid">
              {books.map((book) => {
                const excerpt = shortLine(book.proposition) || BOOK_LINE_EMPTY;
                return (
                  <button
                    key={book.id}
                    type="button"
                    className={`bs-tile ${bookDraft?.id === book.id ? 'bs-tile--active' : ''}`}
                    onClick={() => openBookCard(book)}
                  >
                    <div className="bs-tile__header">
                      <h3 className="bs-tile__title">{book.title}</h3>
                      <span className="bs-tile__badge">{bookStatusLabel(book)}</span>
                    </div>
                    <p className="bs-tile__preview">{excerpt}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="bs-grid">
          {people.length === 0 ? renderEmptyTile('people', 'People') : (
            <div className="bs-tile bs-tile--container">
              <div className="bs-tile__header">
                <h3 className="bs-tile__title">People</h3>
              </div>
              <ul className="bs-tile__instances">
                {people.map((card) => (
                  <li key={card.id}>
                    <button
                      type="button"
                      className={`bs-tile__instance ${draft?.id === card.id ? 'bs-tile__instance--active' : ''}`}
                      onClick={() => startDraft('people', card)}
                    >
                      {card.name}
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" className="bs-add-link" onClick={() => startDraft('people')}>
                + New person
              </button>
            </div>
          )}

          {world ? (
            <button
              type="button"
              className={`bs-tile ${draft?.id === world.id ? 'bs-tile--active' : ''}`}
              onClick={() => startDraft('world', world)}
            >
              <div className="bs-tile__header">
                <h3 className="bs-tile__title">World</h3>
              </div>
              <p className="bs-tile__preview">{world.description || SNAPSHOT_EMPTY}</p>
            </button>
          ) : renderEmptyTile('world', 'World')}
        </div>

        <div className="bs-custom-section">
          <h2 className="bs-custom-section__title">Custom</h2>
          {custom.length === 0 && draft?.kind !== 'custom' && (
            <p className="bs-tile__cta">No custom notes yet.</p>
          )}
          <div className="bs-grid">
            {custom.map((card) => (
              <button
                key={card.id}
                type="button"
                className={`bs-tile ${draft?.id === card.id ? 'bs-tile--active' : ''}`}
                onClick={() => startDraft('custom', card)}
              >
                <div className="bs-tile__header">
                  <h3 className="bs-tile__title">{card.name}</h3>
                </div>
                <p className="bs-tile__preview">{card.description}</p>
              </button>
            ))}
            <button type="button" className="bs-tile bs-tile--empty" onClick={() => startDraft('custom')}>
              <div className="bs-tile__header"><h3 className="bs-tile__title">+ Add custom note</h3></div>
              <p className="bs-tile__cta">{MAKE_A_CARD}</p>
            </button>
          </div>
        </div>
      </div>

      {bookDraft && (
        <div className="bs-tile bs-tile__add-form" style={{ marginTop: 20 }}>
          <div className="bs-edit__field">
            <label className="bs-edit__label" htmlFor="series-book-title">Title</label>
            <input
              id="series-book-title"
              className="bs-edit__input"
              value={bookDraft.title}
              onChange={(e) => setBookDraft({ ...bookDraft, title: e.target.value })}
            />
          </div>
          <div className="bs-edit__field">
            <label className="bs-edit__label" htmlFor="series-book-about">{BOOK_LINE_LABEL}</label>
            <textarea
              id="series-book-about"
              className="bs-edit__input"
              rows={4}
              value={bookDraft.proposition}
              onChange={(e) => setBookDraft({
                ...bookDraft,
                proposition: clampProposition(e.target.value),
              })}
              placeholder={BOOK_LINE_PLACEHOLDER}
            />
            <p className="bs-tile__cta">
              {wordCount(bookDraft.proposition)} / {PROPOSITION_WORD_CAP} words
            </p>
          </div>
          <p className="bs-tile__cta">{bookStatusLabel(bookDraft)}</p>
          <div className="bs-tile__add-form-actions">
            <button
              type="button"
              className="bs-btn bs-btn--primary bs-btn--small"
              onClick={saveBookDraft}
              disabled={saving}
            >
              Save
            </button>
            <button
              type="button"
              className="bs-btn bs-btn--secondary bs-btn--small"
              onClick={() => setBookDraft(null)}
            >
              Cancel
            </button>
            {bookDraft.status === 'started' ? (
              <button
                type="button"
                className="bs-btn bs-btn--secondary bs-btn--small"
                onClick={openBook}
              >
                Open this book
              </button>
            ) : (
              <button
                type="button"
                className="bs-btn bs-btn--secondary bs-btn--small"
                onClick={startBook}
                disabled={startingId === bookDraft.id}
              >
                {startingId === bookDraft.id ? 'Starting…' : 'Start this book'}
              </button>
            )}
          </div>
          {justStartedId === bookDraft.id && (
            <p className="bs-tile__cta" role="status">{START_NOTE}</p>
          )}
        </div>
      )}

      {draft && (
        <div className="bs-tile bs-tile__add-form" style={{ marginTop: 20 }}>
          <div className="bs-edit__field">
            <label className="bs-edit__label" htmlFor="series-card-name">
              {draft.kind === 'world' ? 'World' : 'Name'}
            </label>
            <input
              id="series-card-name"
              className="bs-edit__input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={draft.kind === 'people' ? 'Who is this?' : 'Name this note'}
              autoFocus={draft.kind !== 'world'}
            />
          </div>
          <div className="bs-edit__field">
            <label className="bs-edit__label" htmlFor="series-card-body">Snapshot</label>
            <textarea
              id="series-card-body"
              className="bs-edit__input"
              rows={5}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="A compact statement. What you need is still in the series chat until you write this."
            />
          </div>
          {existingDraft && writeupReady && !showWriteup && (
            <button
              type="button"
              className="bs-add-link"
              onClick={() => setShowWriteup(true)}
            >
              {OPEN_WRITEUP}
            </button>
          )}
          {(showWriteup || !existingDraft) && (
            <div className="bs-edit__field">
              <label className="bs-edit__label" htmlFor="series-card-writeup">
                Long write-up
              </label>
              <textarea
                id="series-card-writeup"
                className="bs-edit__input"
                rows={10}
                value={draft.writeup}
                onChange={(e) => setDraft({ ...draft, writeup: e.target.value })}
                placeholder="Optional. Same note, one save. Leave blank for a thin note."
              />
            </div>
          )}
          <div className="bs-tile__add-form-actions">
            <button type="button" className="bs-btn bs-btn--primary bs-btn--small" onClick={saveDraft} disabled={saving}>
              Save
            </button>
            <button
              type="button"
              className="bs-btn bs-btn--secondary bs-btn--small"
              onClick={() => { setDraft(null); setShowWriteup(false); }}
            >
              Cancel
            </button>
            {existingDraft && (
              <button type="button" className="bs-btn bs-btn--danger bs-btn--small" onClick={deleteDraft} disabled={saving}>
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
